/**
 * XION Medical product data — Camera processors (laryngo-stroboscopy + surgery)
 * Content sourced from xion-medical.com (split from completeProductsData.js).
 */

export const laryngoStroboscopyProcessors = {
  id: 'camera-processors-for-laryngo-stroboscopy',
  name: 'Camera Processors for Laryngo-Stroboscopy',
  slug: 'camera-processors-for-laryngo-stroboscopy',
  category: 'Camera Processors',
  description: 'For many years, XION has been defining the technical standard for stroboscopic examinations of the vocal apparatus. Current EndoSTROBE systems based on the adaptive Spectar camera platform facilitate convenient visualisation of the moving vocal folds in unrivalled image quality. Camera heads and various flexible video endoscopes are available for this purpose.',
  overview: 'In addition to the camera heads designed for general endoscopic applications with fixed focal length or zoom lens, special camera heads with integrated microphone are also available for voice diagnostic examinations. XION\'s professional image enhancement technology (PIET) extends the diagnostic imaging system by adding three visualization technologies that can be applied depending on the respective situation. Modes PIET lumino, PIET chromo and PIET spectro are available in all resolutions of 2D and 3D endoscopy. The EGG module integrated into the camera systems supplements the optical findings by means of the diagnostic options provided through electro-glottography. The XION 3D Laryngoscope now facilitates three-dimensional, stroboscopic visualisation of the movement of the entire voice producing system. Conventional surgical microscopes, e.g. from ZEISS or LEICA, can be seamlessly integrated into the EndoSTROBE Spectar system with the 3D microscope camera. The devices of the EndoSTROBE Spectar series are available in versions with and without integrated LED light source, 3D/4K functionality or EGG.',
  products: [
    {
      id: 'endostrobe-pl-spectar',
      name: 'EndoSTROBE PL Spectar',
      slug: 'endostrobe-pl-spectar',
      tagline: 'Fully featured processor for all video-stroboscopic and surgical applications',
      description: 'Fully featured processor for all video-stroboscopic and surgical applications for flexible video endoscopes and camera heads up to 4K/UHD resolution, PIET, 3D functionality, integrated EGG, integrated LED high power light source.',
      features: [
        'Universal processor for 2D & 3D endoscopy with native sensor resolutions up to 4K (4× HD)',
        'Noiseless, flicker-free stroboscopy in 4K, HD and 3D — including world-first 3D stroboscopy',
        'PIET Professional Image Enhancement Technology (lumino · chromo · spectro)',
        'Integrated LED high-power light source (approx. 72 W, ~60,000 h, ~6,500 K)',
        'Real-time EGG (electroglottography) overlaid on the live image',
        'Patented light-intensity control prevents burns; picture-in-picture function',
        'Consistent, future-proof Spectar Connector for all 2D/3D camera heads & video endoscopes',
        'Minimal image latency; minimal noise even at full power (<30 dB at 1 m)',
        'Highest patient safety via laser-optical galvanic isolation inside the processor',
        'XION PowerControl central power-on; programmable, OR-compatible wireless footswitch',
        'XION safety concept avoids loss of monitor image if the computer crashes'
      ],
      specifications: {
        resolution: ['SD', 'Full HD', '4K (4× HD)'],
        features: ['2D & 3D', 'PIET', 'EGG', 'Integrated LED'],
        lightSource: '~72 W LED · ~60,000 h · ~6,500 K',
        stroboscopy: 'approx. 80–1,000 Hz · noiseless & flicker-free',
        dimensions: '350 × 108 × 365 mm (W×H×D)',
        connectivity: 'Universal Spectar Connector · DVI-D / 3G-SDI'
      },
      images: ['/products/transparent/endostrobe-pl-spectar-web.png']
    },
    {
      id: 'endostrobe-el-spectar',
      name: 'EndoSTROBE EL Spectar',
      slug: 'endostrobe-el-spectar',
      tagline: 'Processor for video-stroboscopic and surgical applications',
      description: 'Processor for all video-stroboscopic and surgical applications for flexible video endoscopes and camera heads up to HD resolution, integrated EGG, integrated LED high power light source.',
      features: [
        'FullHD resolution support',
        'PIET (Professional Image Enhancement Technology)',
        'Integrated LED high power light source',
        'Integrated EGG (Electroglottography)',
        'Silent and artefact-free stroboscopy',
        'Real-time EGG with superimposition on live image'
      ],
      specifications: {
        resolution: ['FullHD'],
        features: ['PIET', 'EGG', 'LED Light Source'],
        connectivity: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/endostrobe-el-spectar-web.png']
    },
    {
      id: 'endostrobe-e-spectar',
      name: 'EndoSTROBE E Spectar',
      slug: 'endostrobe-e-spectar',
      tagline: 'Processor for video-stroboscopic and surgical applications',
      description: 'Processor for all video-stroboscopic and surgical applications for flexible video endoscopes and camera heads up to HD resolution.',
      features: [
        'FullHD resolution support',
        'PIET (Professional Image Enhancement Technology)',
        'Silent and artefact-free stroboscopy',
        'Compatible with all Spectar camera heads and flexible endoscopes',
        'Advanced image processing for voice diagnostics'
      ],
      specifications: {
        resolution: ['FullHD'],
        features: ['PIET'],
        connectivity: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/endostrobe-e-spectar-web.png']
    }
  ]
};

export const surgeryProcessors = {
  id: 'camera-processors-for-surgery',
  name: 'Camera Processors for Surgery',
  slug: 'camera-processors-for-surgery',
  category: 'Camera Processors',
  description: 'Equipped for native sensor resolutions up to 4K/UHD, MATRIX P Spectar Camera Processors offer new possibilities for rigid and flexible endoscopy with the usual ease of use. The overall modular concept and the economic endoscope attachments make it possible to also subsequently expand the MATRIX P / PL Spectar Processors to a state-of-the-art 3D Endoscope System at just moderate cost.',
  overview: 'Conventional surgical microscopes, e.g. from ZEISS or LEICA, can be seamlessly integrated into MATRIX P / PL Spectar Systems with the 3D microscope camera. XION\'s professional image enhancement technology (PIET) extends the diagnostic imaging system by adding three visualization technologies that can be applied depending on the respective situation. Modes PIET lumino, PIET chromo and PIET spectro are available in all resolutions of 2D and 3D endoscopy. MATRIX Spectar series devices are available in versions with and without integrated LED light source and 3D/4K functionality.',
  products: [
    {
      id: 'matrix-pl-spectar',
      name: 'MATRIX PL Spectar',
      slug: 'matrix-pl-spectar',
      tagline: 'Fully featured processor for all endoscopic applications',
      description: 'Fully featured processor for all endoscopic applications for flexible video endoscopes and camera heads up to 4K/UHD resolution, PIET, 3D functionality, integrated LED high power light source.',
      features: [
        'FullHD resolution support',
        '4K/UHD resolution support',
        '3D functionality',
        'PIET (Professional Image Enhancement Technology)',
        'Integrated LED high power light source',
        'Universal connectivity for flexible video endoscopes and camera heads',
        'Seamless integration with surgical microscopes (ZEISS, LEICA)',
        'Modular expansion to 3D endoscopy system'
      ],
      specifications: {
        resolution: ['FullHD', '4K/UHD'],
        features: ['3D', 'PIET', 'LED Light Source'],
        connectivity: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/csm-matrix-pl-spectar-web-3bd7424485.png']
    },
    {
      id: 'matrix-p-spectar',
      name: 'MATRIX P Spectar',
      slug: 'matrix-p-spectar',
      tagline: 'Fully featured processor for all endoscopic applications',
      description: 'Fully featured processor for all endoscopic applications for flexible video endoscopes and camera heads up to 4K/UHD resolution, PIET, 3D functionality.',
      features: [
        'FullHD resolution support',
        '4K/UHD resolution support',
        '3D functionality',
        'PIET (Professional Image Enhancement Technology)',
        'Universal connectivity for flexible video endoscopes and camera heads',
        'Seamless integration with surgical microscopes',
        'Economic expansion to 3D endoscopy'
      ],
      specifications: {
        resolution: ['FullHD', '4K/UHD'],
        features: ['3D', 'PIET'],
        connectivity: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/matrix-p-spectar-web.png']
    }
  ]
};

export const officeMobileProcessors = {
  id: 'camera-processors-for-office-and-mobile-applications',
  name: 'Camera Processors for Office & Mobile Applications',
  slug: 'camera-processors-for-office-and-mobile-applications',
  category: 'Camera Processors',
  description: 'Thanks to their versatility, compact dimensions and good price-performance ratio, the MATRIX E Spectar and MATRIX EL Spectar camera processors are excellently suited for applications in outpatient departments and medical practices. Combined with the EndoDESK back-wall system or roller stands, they enable compact and, if required, easily manoeuvrable units.',
  overview: 'For genuinely mobile use — e.g. in swallowing diagnostics — the EndoPORTABLE Veezar system pairs a flexible video nasopharyngoscope with a 10\u2033 medical tablet. All processors share the universal Spectar Connector, so camera heads and flexible video endoscopes remain interchangeable across the XION platform.',
  products: [
    {
      id: 'matrix-el-spectar',
      name: 'MATRIX EL Spectar',
      slug: 'matrix-el-spectar',
      tagline: 'Compact office/mobile processor with integrated LED',
      description: 'Processor for all endoscopic applications for flexible video endoscopes and camera heads up to HD resolution, with an integrated LED high-power light source — ideal for outpatient departments and medical practices.',
      features: [
        'Full HD resolution support',
        'Integrated LED high-power light source',
        'Universal Spectar Connector for camera heads & flexible video endoscopes',
        'Compact dimensions and good price-performance ratio',
        'Ideal for office / outpatient endoscopy (EndoDESK, roller stands)'
      ],
      specifications: {
        resolution: ['Full HD'],
        features: ['Integrated LED'],
        connectivity: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/matrix-el-spectar-web.png']
    },
    {
      id: 'matrix-e-spectar',
      name: 'MATRIX E Spectar',
      slug: 'matrix-e-spectar',
      tagline: 'Compact, cost-efficient office/mobile processor',
      description: 'Processor for all endoscopic applications for flexible video endoscopes and camera heads up to HD resolution — the most compact and cost-efficient entry into the Spectar platform for office and outpatient use.',
      features: [
        'Full HD resolution support',
        'Universal Spectar Connector for camera heads & flexible video endoscopes',
        'High-quality image processing',
        'Compact dimensions and excellent price-performance ratio',
        'Ideal for medical practices and outpatient departments'
      ],
      specifications: {
        resolution: ['Full HD'],
        features: [],
        connectivity: 'Universal Spectar Connector'
      },
      images: ['/products/transparent/matrix-e-spectar-web.png']
    }
  ]
};
